import { memo, useEffect, useState } from 'react';
import { useAppStore, type SessionResetPhase } from '../stores/appStore';
import { formatTokenCount } from '../utils/tokenFormatting';

/** Strip provider prefix from model name: "anthropic/claude-opus-4-6" -> "claude-opus-4-6" */
function formatModelName(model: string | null | undefined): string {
  if (!model) return '—';
  const parts = model.split('/');
  return parts.length > 1 ? parts.slice(1).join('/') : model;
}

/** Format thinking level for display: "high" -> "think high", null -> "think off" */
function formatThinkingLevel(level: string | null | undefined): string {
  if (!level || level === 'off' || level === 'none') {
    return 'think off';
  }
  return `think ${level}`;
}

/** Animated status text component for session reset phases */
function AnimatedResetStatus({ phase }: { phase: SessionResetPhase }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger fade-in on mount or phase change
    setVisible(false);
    const timer = requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => cancelAnimationFrame(timer);
  }, [phase]);

  if (!phase) return null;

  const label = phase === 'saving' ? '✨ Saving session...' : '🚀 Starting new session...';

  return (
    <span
      className="session-reset-status"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease-out',
      }}
    >
      {label}
    </span>
  );
}

/** Normal status bar content */
function NormalStatusContent({
  items,
  sessionKey,
  fadingOut,
}: {
  items: string[];
  sessionKey: string;
  fadingOut: boolean;
}) {
  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap"
      style={{
        opacity: fadingOut ? 0 : 1,
        transition: 'opacity 300ms ease-out',
      }}
    >
      {items.map((item, index) => (
        <span key={index} className="flex items-center gap-1.5">
          {index > 0 && (
            <span style={{ color: 'var(--theme-muted-foreground)', opacity: 0.4 }}>·</span>
          )}
          <span className={index === 0 ? 'truncate max-w-[200px]' : ''} title={index === 0 ? sessionKey : undefined}>
            {item}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * BuilderStatusBar — A small status bar showing session info for builder users.
 * Displays: session key, model, thinking level, token usage.
 * During session reset: shows animated "Saving session..." / "Starting new session..."
 * Hidden for team members (non-builder users).
 */
export default memo(function BuilderStatusBar() {
  const userRole = useAppStore((state) => state.userRole);
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const sessions = useAppStore((state) => state.sessions);
  const connected = useAppStore((state) => state.connected);
  const sessionResetPhaseMap = useAppStore((state) => state.sessionResetPhase);
  const sessionContextFill = useAppStore((state) => state.sessionContextFill);

  // Only show for builder users (not team members)
  if (userRole !== 'builder') {
    return null;
  }

  // Only show when connected and there's an active session
  if (!connected || !activeSessionKey) {
    return null;
  }

  // Check if session is in reset phase
  const resetPhase = sessionResetPhaseMap[activeSessionKey] ?? null;

  // Find current session metadata
  const currentSession = sessions.find((s: any) => {
    const key = s.sessionKey ?? s.key ?? s.id;
    return key === activeSessionKey;
  });

  // Extract metadata from session (may not exist in Teams)
  const sessionKey = activeSessionKey ?? '—';
  const model = formatModelName(currentSession?.model);
  const thinkingLevel = formatThinkingLevel(currentSession?.thinkingLevel);

  // Token usage — actual context window fill from the last turn.
  // = input_tokens + cache_read + cache_creation from the most recent assistant message.
  // This is how full the model's context window is right now.
  const contextFill = activeSessionKey ? sessionContextFill[activeSessionKey] : undefined;
  const contextTokens = currentSession?.contextTokens;

  const displayTokens = contextFill;

  let tokenDisplay: string;
  if (displayTokens !== undefined && displayTokens !== null && Number.isFinite(displayTokens)) {
    if (contextTokens && Number.isFinite(contextTokens) && contextTokens > 0) {
      const percentage = Math.round((displayTokens / contextTokens) * 100);
      tokenDisplay = `tokens ${formatTokenCount(displayTokens)}/${formatTokenCount(contextTokens)} (${percentage}%)`;
    } else {
      tokenDisplay = `tokens ${formatTokenCount(displayTokens)}`;
    }
  } else {
    tokenDisplay = 'tokens —';
  }

  // Pipeline badge
  const pipelineId: string | undefined = currentSession?.pipelineId;

  // Build display items - always include session key, conditionally include others
  const items: string[] = [sessionKey];

  // Only show model/thinking/tokens if we have real data
  if (currentSession?.model) {
    items.push(model);
  }
  if (currentSession?.thinkingLevel) {
    items.push(thinkingLevel);
  }
  if (displayTokens !== undefined && displayTokens !== null && Number.isFinite(displayTokens)) {
    items.push(tokenDisplay);
  }

  return (
    <div
      className="px-4 py-1 select-none"
      style={{
        fontSize: '11px',
        fontFamily: 'var(--app-ui-font)',
        color: 'var(--theme-muted-foreground)',
        opacity: 0.7,
        borderTop: '1px solid rgba(255,255,255,0.03)',
        background: 'rgba(0,0,0,0.1)',
        // Ensure consistent height to prevent layout jumps
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {resetPhase ? (
        <AnimatedResetStatus phase={resetPhase} />
      ) : (
        <>
          {pipelineId && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: '3px',
                backgroundColor: 'rgba(255, 152, 0, 0.15)',
                color: 'rgb(255, 152, 0)',
                marginRight: '8px',
                flexShrink: 0,
              }}
            >
              PIPELINE
            </span>
          )}
          <NormalStatusContent items={items} sessionKey={sessionKey} fadingOut={false} />
        </>
      )}
    </div>
  );
});
